import { buildWebsiteUrl } from '@repo/utils/url';

import { getCategories, getFields, getFieldsV2 } from 'strapi/client';
import {
  EnrichedCategoriesInfoItem,
  ToolsListInfo,
  ToolsType,
} from 'strapi/component/categoryList/types';
import { PageType, PageTypes, ToolsPageProps } from 'strapi/page/types';

import { collectImageComponentParams } from '../shared/collector';

import { enrichToolsPages, extraTools } from './config';

const enrichToolsPagesInfoItemsByPageType = async (
  type: PageType | string
): Promise<EnrichedCategoriesInfoItem[]> => {
  let pagesInfo;
  // if old implementation of page
  if (Object.keys(PageType)?.includes(type)) {
    pagesInfo = await getFields<ToolsPageProps>(
      type as PageType,
      ['slug', 'path', 'descriptiveName'],
      {},
      { cover: true }
    );
  } else {
    pagesInfo = await getFieldsV2<ToolsPageProps>({
      pageKey: type,
      fields: ['path', 'descriptiveName'],
      populate: { cover: true },
    });
  }

  if (!pagesInfo || 'error' in pagesInfo) {
    return [];
  }

  const pagesArray = Array.isArray(pagesInfo) ? pagesInfo : [pagesInfo];
  if (!pagesArray?.length) {
    return [];
  }

  return pagesArray.map((y) => ({
    title: y.descriptiveName ?? y.slug,
    url: buildWebsiteUrl(y.path),
    image: collectImageComponentParams(y.cover)!,
  }));
};

const enrichToolsPagesInfoItems = async (
  type: ToolsType
): Promise<EnrichedCategoriesInfoItem[]> => {
  const pagesTypes = enrichToolsPages[type];

  if (!pagesTypes) {
    return [];
  }

  const infoItemsList = [];
  for (const page of pagesTypes) {
    const infoItems = await enrichToolsPagesInfoItemsByPageType(page);
    infoItemsList.push(...infoItems);
  }

  return infoItemsList;
};

export const enrichWithCategoryListParams = async (
  pageType: PageType,
  page: PageTypes
): Promise<PageTypes> => {
  if (
    pageType === PageType.CreateLibraryPage &&
    'categoryList' in page &&
    !!page.categoryList &&
    !!page.categoryList.categoriesInfo
  ) {
    const categories = await getCategories({
      populate: {
        use_cases: {
          fields: ['name', 'create_use_case_page'],
          populate: {
            create_use_case_page: {
              fields: ['cover', 'path'],
              populate: { cover: true },
            },
          },
        },
      },
    });

    if ('error' in categories) {
      return page;
    }

    const categoryListCategories = page.categoryList.categoriesInfo.map(
      (x) => x.category.name
    );

    const missedCategories = categories
      .filter((x) => !categoryListCategories.includes(x.name))
      .map((x) => ({
        icon: undefined,
        description: undefined,
        category: {
          name: x.name,
          use_cases: x.use_cases,
        },
      }));

    page.categoryList.categoriesInfo.push(...missedCategories);

    return page;
  }

  if (
    pageType === PageType.ToolsLibraryPage &&
    'toolsCategoryList' in page &&
    page.toolsCategoryList
  ) {
    const extraCategories: ToolsListInfo[] =
      page.toolsCategoryList.toolsCategoriesInfo
        ?.filter((x) => !!extraTools[x.type] && !!extraTools[x.type]?.length)
        .map((x) => ({
          name: x.name,
          description: x.description,
          icon: x.icon,
          type: x.type,
          enrichedItems: extraTools[x.type] ?? [],
        })) ?? [];

    const toolsCategoriesInfo =
      page.toolsCategoryList.toolsCategoriesInfo?.filter(
        (x) => !!enrichToolsPages[x.type] && !!enrichToolsPages[x.type]?.length
      ) ?? [];

    const categories = await Promise.all(
      toolsCategoriesInfo.map(async (toolsCategoriesInfoElement) => {
        const infoItems = await enrichToolsPagesInfoItems(
          toolsCategoriesInfoElement.type
        );

        if (infoItems.length > 0) {
          return {
            name: toolsCategoriesInfoElement.name,
            description: toolsCategoriesInfoElement.description,
            icon: toolsCategoriesInfoElement.icon,
            type: toolsCategoriesInfoElement.type,
            enrichedItems: infoItems,
          };
        }
        return null;
      })
    );

    const mergedCategories: ToolsListInfo[] = categories.filter(
      (x) => x !== null
    );
    for (const extraCategory of extraCategories) {
      const category = mergedCategories.find(
        (x) => x.name === extraCategory.name
      );

      if (category) {
        category.enrichedItems.push(...extraCategory.enrichedItems);
      } else {
        mergedCategories.push(extraCategory);
      }
    }
    page.toolsCategoryList.toolsCategoriesInfo = mergedCategories;

    return page;
  }

  return page;
};
